import { useRef, useState } from 'react'
import Swal from 'sweetalert2'
import IPButton from '../../../../components/IPButton'
import IPModal from '../../../../components/IPModal'
import { IException } from '../../../../base/models'
import { APPLE_DEP_TEMPLATE_URL, IAppleDepParsedTemplate, parseAppleDepTemplate } from '../models/excelTemplate'
import { IAppleDepEnvironment, IAppleDepExcelImportResult } from '../models/models'
import { ImportAppleDepExcelApi } from '../services/appleDepApi'

interface ExcelImportModalProps {
  show: boolean
  environment?: IAppleDepEnvironment
  showLoading: () => void
  hideLoading: () => void
  onClose: () => void
  /** Kayıt yazıldıktan sonra cihaz listesini tazelemek için. */
  onImported: () => void
}

/**
 * Apple'ın Distributor Enrollment Form şablonuyla toplu müşteri ataması.
 *
 * Akış: şablonu indir → doldur → dosyayı seç (tarayıcıda ayrıştırılır) →
 * **Önizle** (sunucu doğrular, hiçbir kayıt değişmez) → **Aktar**.
 *
 * Excel'den cihaz yaratılmaz: seri numaraları SAP'ten gelen havuzla eşleştirilir,
 * havuzda olmayan satır reddedilir. Bu işlem Apple'a gönderim yapmaz.
 */
export default function ExcelImportModal(props: ExcelImportModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [fileName, setFileName] = useState('')
  const [parsed, setParsed] = useState<IAppleDepParsedTemplate | undefined>(undefined)
  const [useDealerOwn, setUseDealerOwn] = useState(false)
  const [result, setResult] = useState<IAppleDepExcelImportResult | undefined>(undefined)

  const canUseDealerOwn = props.environment?.CanUseDealerOwnOrganization ?? false
  const canUseEndCustomer = props.environment?.CanUseEndCustomerOrganization ?? true
  // Yalnızca kendi kurumu moduysa seçenek sunulmaz, doğrudan uygulanır.
  const dealerOwnForced = canUseDealerOwn && !canUseEndCustomer
  const effectiveUseDealerOwn = dealerOwnForced || useDealerOwn

  const showError = (err: any, fallback: string) => {
    const exception = err as IException
    Swal.fire({
      icon: 'error',
      title: exception?.description ?? fallback,
      showConfirmButton: true,
      allowOutsideClick: false,
    })
  }

  const reset = () => {
    setFileName('')
    setParsed(undefined)
    setResult(undefined)
    setUseDealerOwn(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const close = () => {
    reset()
    props.onClose()
  }

  const onFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setResult(undefined)
    setFileName(file.name)

    try {
      const content = await parseAppleDepTemplate(file)

      if (content.SerialNumbers.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Seri numarası bulunamadı',
          text: 'Şablonun ikinci sayfasına (Serial Numbers) seri numaralarını alt alta yazın.',
        })
      }

      setParsed(content)
    } catch (err: any) {
      setParsed(undefined)
      showError(err, 'Dosya okunamadı. Şablonu değiştirmeden doldurup tekrar deneyin.')
    }
  }

  const runImport = async (validateOnly: boolean) => {
    if (!parsed) {
      Swal.fire({ icon: 'warning', title: 'Önce bir dosya seçin.' })
      return
    }

    if (parsed.SerialNumbers.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Dosyada seri numarası yok.' })
      return
    }

    props.showLoading()
    try {
      const api: ImportAppleDepExcelApi = new ImportAppleDepExcelApi({})
      const response = await api.importExcel({
        CustomerName: parsed.CustomerName,
        OrganizationId: parsed.OrganizationId,
        UseDealerOwnOrganization: effectiveUseDealerOwn,
        SalesOrderNumber: parsed.SalesOrderNumber,
        SalesOrderDate: parsed.SalesOrderDate,
        PoNumber: parsed.PoNumber,
        TransactionType: parsed.TransactionType,
        SerialNumbers: parsed.SerialNumbers,
        FileName: fileName,
        ValidateOnly: validateOnly,
      })

      setResult(response)

      if (!validateOnly) {
        props.onImported()

        Swal.fire({
          icon: response.SkippedCount > 0 ? 'warning' : 'success',
          title: 'Aktarım tamamlandı',
          html: `
            <div style="text-align:left">
              <p><strong>Müşteri:</strong> ${response.CustomerName ?? '-'} (${response.OrganizationId ?? '-'})</p>
              <p><strong>Atanan cihaz:</strong> ${response.AssignedCount}</p>
              ${response.SkippedCount ? `<p><strong>Atlanan:</strong> ${response.SkippedCount}</p>` : ''}
              ${response.CustomerCreated ? '<p>Bu Organization ID için yeni müşteri kaydı oluşturuldu.</p>' : ''}
            </div>`,
        })
      }
    } catch (err: any) {
      showError(err, validateOnly ? 'Önizleme alınamadı.' : 'Aktarım başarısız oldu.')
    } finally {
      props.hideLoading()
    }
  }

  return (
    <IPModal show={props.show} onClose={close} hideConfirm width={980} title='Excel ile Toplu Müşteri Atama'>
      <div className='appleDepImport'>
        <div className='appleDepInfoBox'>
          Bu işlem <strong>Apple'a gönderim yapmaz</strong>; yalnızca seçtiğiniz cihazlara müşteri
          ataması yapar. Gönderim için atama sonrası <strong>Apple'a Gönder</strong> butonunu
          kullanmanız gerekir. Şablondaki seri numaraları, Index'in size sattığı cihazlarla
          eşleştirilir — listede olmayan seri numarası kabul edilmez.
        </div>

        <div className='appleDepImportStep'>
          <span className='appleDepImportStepNo'>1</span>
          <div>
            <div className='appleDepImportStepTitle'>Şablonu indirin</div>
            <a className='appleDepTemplateLink' href={APPLE_DEP_TEMPLATE_URL} download>
              apple-dep-yukleme-sablonu.xlsx
            </a>
            <div className='appleDepHint'>
              Apple'ın Distributor Enrollment Form düzeni. Etiketleri değiştirmeyin; seri numaralarını
              ikinci sayfaya yazın.
            </div>
          </div>
        </div>

        <div className='appleDepImportStep'>
          <span className='appleDepImportStepNo'>2</span>
          <div>
            <div className='appleDepImportStepTitle'>Doldurduğunuz dosyayı seçin</div>
            <input
              ref={fileInputRef}
              type='file'
              accept='.xlsx,.xls'
              onChange={onFileSelected}
              className='appleDepFileInput'
            />
            {fileName && <div className='appleDepHint'>Seçilen dosya: {fileName}</div>}
          </div>
        </div>

        {parsed && (
          <>
            <div className='appleDepImportStep'>
              <span className='appleDepImportStepNo'>3</span>
              <div style={{ width: '100%' }}>
                <div className='appleDepImportStepTitle'>Okunan bilgiler</div>

                <div className='appleDepDetailGrid'>
                  <div>
                    <span className='appleDepDetailLabel'>Kurum Adı</span>
                    <span>{parsed.CustomerName || '-'}</span>
                  </div>
                  <div>
                    <span className='appleDepDetailLabel'>Organization ID</span>
                    <span>
                      <strong>{effectiveUseDealerOwn
                        ? props.environment?.DealerOwnOrganizationId ?? '-'
                        : parsed.OrganizationId || '-'}</strong>
                    </span>
                  </div>
                  <div>
                    <span className='appleDepDetailLabel'>Bayi Sipariş No</span>
                    <span>{parsed.SalesOrderNumber || '-'}</span>
                  </div>
                  <div>
                    <span className='appleDepDetailLabel'>Satış Tarihi</span>
                    <span>{parsed.SalesOrderDate || '-'}</span>
                  </div>
                  <div>
                    <span className='appleDepDetailLabel'>Müşteri PO</span>
                    <span>{parsed.PoNumber || '-'}</span>
                  </div>
                  <div>
                    <span className='appleDepDetailLabel'>İşlem Tipi</span>
                    <span>{parsed.TransactionType || '-'}</span>
                  </div>
                  <div>
                    <span className='appleDepDetailLabel'>Seri Numarası</span>
                    <span>
                      <strong>{parsed.SerialNumbers.length}</strong> adet
                    </span>
                  </div>
                </div>

                {canUseDealerOwn && canUseEndCustomer && (
                  <label className='appleDepCheckLine'>
                    <input
                      type='checkbox'
                      checked={useDealerOwn}
                      onChange={e => {
                        setUseDealerOwn(e.target.checked)
                        setResult(undefined)
                      }}
                    />{' '}
                    Şablondaki Organization ID yerine <strong>kendi kurumuma</strong> kaydet
                    {props.environment?.DealerOwnOrganizationId
                      ? ` (${props.environment.DealerOwnOrganizationId})`
                      : ''}
                  </label>
                )}

                {dealerOwnForced && (
                  <div className='appleDepHint'>
                    Bu bayide cihazlar yalnızca kendi kurumunuza kaydedilir; şablondaki Organization ID
                    kullanılmaz.
                  </div>
                )}
              </div>
            </div>

            <div className='appleDepImportButtons'>
              <IPButton text='Önizle' className='appleDepGhostButton' onClick={() => runImport(true)} />
              <IPButton
                text='Aktar'
                className='appleDepPrimaryButton'
                onClick={() => runImport(false)}
                disabled={!result || result.AssignedCount === 0}
              />
            </div>
            {!result && (
              <div className='appleDepHint appleDepImportHintRight'>
                Aktarmadan önce önizleme alın; hangi cihazların eşleştiğini görürsünüz.
              </div>
            )}
          </>
        )}

        {result && (
          <div className='appleDepImportResult'>
            <div className='appleDepImportSummary'>
              <span>
                Toplam <strong>{result.TotalRows}</strong>
              </span>
              <span>
                Eşleşen <strong>{result.MatchedCount}</strong>
              </span>
              <span className='appleDepImportOk'>
                {result.ValidateOnly ? 'Atanabilir' : 'Atanan'} <strong>{result.AssignedCount}</strong>
              </span>
              {result.SkippedCount > 0 && (
                <span className='appleDepImportSkip'>
                  Atlanan <strong>{result.SkippedCount}</strong>
                </span>
              )}
              {result.ValidateOnly && <span className='appleDepBadge appleDepBadgeInfo'>Önizleme</span>}
            </div>

            <div className='appleDepTableWrapper'>
              <table className='table table-hover appleDepTable'>
                <thead>
                  <tr>
                    <th>Seri No / IMEI</th>
                    <th>Ürün</th>
                    <th>Sipariş No</th>
                    <th>Sonuç</th>
                  </tr>
                </thead>
                <tbody>
                  {result.Rows.map(row => (
                    <tr key={row.SerialNumber} className={row.IsAssigned ? '' : 'appleDepRowFailed'}>
                      <td>
                        <strong>{row.SerialNumber}</strong>
                      </td>
                      <td>{row.ProductDescription ?? '-'}</td>
                      <td>{row.SapOrderNo ?? '-'}</td>
                      <td>
                        {row.IsAssigned ? (
                          <span className='appleDepBadge appleDepBadgeSuccess'>
                            {result.ValidateOnly ? 'Atanabilir' : 'Atandı'}
                          </span>
                        ) : (
                          <>
                            <span className='appleDepBadge appleDepBadgeDanger'>Atlandı</span>
                            <div className='appleDepBlockReason'>{row.Message}</div>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </IPModal>
  )
}
